/** The tenant's provider credentials: add one, check it, replace its key in place, delete it. Keys are
 * write-only: the API never returns them, and the form clears them once sent. The fields each provider
 * needs come from the catalogue (`credential_fields`). */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { useId, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { useApiQuery } from "../app/Page";
import { useBff, useMe } from "../app/session";
import { useFormat, useT, type MessageId } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState } from "../ui/states";
import { Table } from "../ui/Table";

type Credential = Schemas["CredentialOut"];
type Verdict = Schemas["VerifyOut"];
type Manifest = Schemas["ProviderManifest"];
/** One provider name across its kinds (Deepgram is STT and TTS): one credential serves both. */
export interface Provider { name: string; label: string; fields: string[] }

const FIELD =
  "rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm disabled:opacity-60";
const OPTIONAL = new Set(["workspace_id"]);
const PLAIN = new Set(["base_url", "region", "workspace_id", "username"]); // not secret: shown as typed
const KNOWN = new Set(["api_key", "base_url", "region", "workspace_id", "service_account_json"]);

/** The catalogue's providers a tenant keeps a key for: not the test providers, not its own model endpoints. */
export function providersOf(manifests: Manifest[]): Provider[] {
  const byName = new Map<string, { labels: string[]; fields: string[] }>();
  for (const m of manifests) {
    const fields = m.credential_fields ?? ["api_key"]; // the catalogue's default
    if (m.testing || m.name === "endpoint" || fields.length === 0) continue;
    const seen = byName.get(m.name) ?? { labels: [], fields: [] };
    if (!seen.labels.includes(m.display_name)) seen.labels.push(m.display_name);
    for (const f of fields) if (!seen.fields.includes(f)) seen.fields.push(f);
    byName.set(m.name, seen);
  }
  return [...byName].map(([name, p]) => ({ name, label: p.labels.join(" · "), fields: p.fields }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function useCredentialsChanged() {
  const queries = useQueryClient();
  const tenant = useMe().tenant_id;
  return () => queries.invalidateQueries({ queryKey: ["credentials", tenant] });
}

function useFieldLabel() {
  const t = useT();
  return (field: string) => {
    const label = KNOWN.has(field) ? t(`credentials.field.${field}` as MessageId) : field;
    return OPTIONAL.has(field) ? t("credentials.optional", { label }) : label;
  };
}

export function Credentials({ credentials }: { credentials: Credential[] }) {
  const t = useT();
  const format = useFormat();
  const catalogue = useApiQuery("providers:all", (api) => api.GET("/v1/providers", { params: { query: { include_testing: true } } }));
  const providers = providersOf(catalogue.data?.data ?? []);
  const labelOf = (name: string) => providers.find((p) => p.name === name)?.label ?? name;
  const [replacing, setReplacing] = useState<Credential | null>(null);
  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-[var(--color-muted)]">{t("credentials.intro")}</p>
      {credentials.length === 0 ? (
        <Empty title={t("credentials.empty")}><p>{t("credentials.emptyBody")}</p></Empty>
      ) : (
        <Table
          caption={t("credentials.title")}
          rows={credentials}
          rowKey={(c) => c.id}
          columns={[
            { header: t("credentials.col.name"), cell: (c) => <span className="font-mono font-medium">{c.name}</span> },
            { header: t("credentials.col.provider"), cell: (c) => labelOf(c.provider) },
            { header: t("credentials.col.fields"), cell: (c) => <span className="font-mono text-xs">{c.secret_fields.join(", ")}</span> },
            { header: t("credentials.col.check"), cell: (c) => <Check credential={c} /> },
            { header: t("credentials.col.updated"), cell: (c) => format.dateTime(c.updated_at) },
            {
              header: t("credentials.col.actions"),
              cell: (c) => (
                <div className="flex flex-wrap gap-1">
                  <Button variant="ghost" onClick={() => setReplacing(c)} aria-label={t("credentials.replaceWho", { name: c.name })}>
                    {t("credentials.replace")}
                  </Button>
                  <Delete credential={c} />
                </div>
              ),
            },
          ]}
        />
      )}
      {replacing && (
        <Replace key={replacing.id} credential={replacing} fields={providers.find((p) => p.name === replacing.provider)?.fields}
          onDone={() => setReplacing(null)} />
      )}
      <Add providers={providers} taken={credentials.map((c) => c.name)} />
    </div>
  );
}

const KNOWN_STATUS = new Set(["ok", "failed", "unreachable", "unsupported", "none"]);

/** Ask the provider whether a saved credential's key works (the API's verify). */
function useVerify() {
  const { api } = useBff();
  return (credentialId: string) =>
    data(api.POST("/v1/credentials/{credential_id}/verify", { params: { path: { credential_id: credentialId } } }));
}

/** A check's outcome as a sentence: the status, and the provider's reason when there is one. */
function Verdict({ verdict, status }: { verdict?: Verdict; status: string }) {
  const t = useT();
  const label = KNOWN_STATUS.has(status) ? t(`credentials.status.${status}` as MessageId) : status;
  return <>{verdict?.detail ? `${label}: ${verdict.detail}` : label}</>;
}

function Check({ credential }: { credential: Credential }) {
  const t = useT();
  const changed = useCredentialsChanged();
  const run = useVerify();
  const verify = useMutation({ mutationFn: () => run(credential.id), onSuccess: changed });
  const status = verify.data?.status ?? credential.verify_status ?? "none";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm" role={verify.isSuccess ? "status" : undefined}>
        <Verdict verdict={verify.data} status={status} />
      </span>
      <Button variant="secondary" disabled={verify.isPending} onClick={() => verify.mutate()}
        aria-label={t("credentials.checkWho", { name: credential.name })}>
        {t("credentials.check")}
      </Button>
      {verify.isError && <ErrorState error={verify.error} />}
    </div>
  );
}

function Delete({ credential }: { credential: Credential }) {
  const t = useT();
  const { api } = useBff();
  const changed = useCredentialsChanged();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => data(api.DELETE("/v1/credentials/{credential_id}", { params: { path: { credential_id: credential.id } } })),
    onSuccess: changed,
  });
  if (!confirming) {
    return (
      <Button variant="ghost" onClick={() => setConfirming(true)} aria-label={t("credentials.deleteWho", { name: credential.name })}>
        {t("credentials.delete")}
      </Button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm">{t("credentials.deleteConfirm", { name: credential.name })}</span>
      <Button variant="secondary" disabled={remove.isPending} onClick={() => remove.mutate()}>{t("credentials.deleteYes")}</Button>
      <Button variant="ghost" onClick={() => setConfirming(false)}>{t("common.cancel")}</Button>
      {remove.isError && <ErrorState error={remove.error} />}
    </div>
  );
}

/** The provider's fields, each its own input: keys masked, a service account's JSON in a text area. */
function SecretFields({ id, fields, values, onChange, required }: {
  id: string;
  fields: string[];
  values: Record<string, string>;
  onChange: (field: string, value: string) => void;
  required: boolean;
}) {
  const label = useFieldLabel();
  return (
    <>
      {fields.map((f) => (
        <div key={f} className="space-y-1">
          <label htmlFor={`${id}-${f}`} className="block text-xs text-[var(--color-muted)]">{label(f)}</label>
          {f === "service_account_json" ? (
            <textarea id={`${id}-${f}`} value={values[f] ?? ""} onChange={(e) => onChange(f, e.target.value)}
              required={required} rows={4} spellCheck={false} autoComplete="off" className={`${FIELD} w-96 font-mono`} />
          ) : (
            <input id={`${id}-${f}`} type={PLAIN.has(f) ? "text" : "password"} value={values[f] ?? ""}
              onChange={(e) => onChange(f, e.target.value)} required={required && !OPTIONAL.has(f)}
              autoComplete={PLAIN.has(f) ? "off" : "new-password"} spellCheck={false} className={`${FIELD} w-72`} />
          )}
        </div>
      ))}
    </>
  );
}

const filled = (values: Record<string, string>) =>
  Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v));

function Replace({ credential, fields, onDone }: { credential: Credential; fields?: string[]; onDone: () => void }) {
  const t = useT();
  const id = useId();
  const { api } = useBff();
  const changed = useCredentialsChanged();
  const verify = useVerify();
  const [values, setValues] = useState<Record<string, string>>({});
  const [nothing, setNothing] = useState(false);
  const all = [...new Set([...(fields ?? []), ...credential.secret_fields])];
  const replace = useMutation({
    mutationFn: async (secret: Record<string, string>) => {
      await data(api.PUT("/v1/credentials/{credential_id}/secret", {
        params: { path: { credential_id: credential.id } }, body: { secret },
      }));
      return verify(credential.id).catch(() => undefined); // the key is saved even if the check cannot run
    },
    onSuccess: async () => {
      setValues({});
      await changed();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const secret = filled(values);
    setNothing(Object.keys(secret).length === 0);
    if (Object.keys(secret).length) replace.mutate(secret);
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-md border border-[var(--color-border)] p-3" aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`} className="text-sm font-medium">{t("credentials.replaceWho", { name: credential.name })}</h3>
      <div className="flex flex-wrap items-end gap-2">
        <SecretFields id={id} fields={all} values={values} required={false}
          onChange={(f, v) => setValues((old) => ({ ...old, [f]: v }))} />
        <Button type="submit" disabled={replace.isPending}>{t("credentials.replaceSave")}</Button>
        <Button variant="ghost" onClick={onDone}>{t("common.cancel")}</Button>
      </div>
      <p className="text-xs text-[var(--color-muted)]">{t("credentials.replaceHint", { name: credential.name })}</p>
      {nothing && <p role="alert" className="text-sm">{t("credentials.replaceNothing")}</p>}
      {replace.isPending && <p className="text-sm text-[var(--color-muted)]">{t("credentials.checking")}</p>}
      {replace.isSuccess && (
        <p role="status" className="text-sm">
          {t("credentials.replaced", { name: credential.name })}{" "}
          {replace.data && <Verdict verdict={replace.data} status={replace.data.status} />}
        </p>
      )}
      {replace.isError && <ErrorState error={replace.error} />}
    </form>
  );
}

/** A name agents do not use yet: the provider's own name first (the name templates and the copilot use). */
function freeName(provider: string, taken: string[]): string {
  if (!taken.includes(provider)) return provider;
  for (let n = 2; ; n++) if (!taken.includes(`${provider}-${n}`)) return `${provider}-${n}`;
}

function Add({ providers, taken }: { providers: Provider[]; taken: string[] }) {
  const t = useT();
  const id = useId();
  const { api } = useBff();
  const changed = useCredentialsChanged();
  const verify = useVerify();
  // ?add=deepgram (the builder's "add a credential" link) opens the form at that provider
  const asked = useRouterState({ select: (s) => (s.location.search as Record<string, unknown>).add });
  const [provider, setProvider] = useState(typeof asked === "string" ? asked : "");
  const [name, setName] = useState(typeof asked === "string" ? freeName(asked, taken) : "");
  const [values, setValues] = useState<Record<string, string>>({});
  const chosen = providers.find((p) => p.name === provider);
  const add = useMutation({
    mutationFn: async () => {
      const created = await data(api.POST("/v1/credentials", { body: { provider, name: name.trim(), secret: filled(values) } }));
      const verdict = await verify(created.id).catch(() => undefined); // saved even if the check cannot run
      return { created, verdict };
    },
    onSuccess: async () => {
      setProvider("");
      setName("");
      setValues({});
      await changed();
    },
  });
  const pick = (next: string) => {
    setProvider(next);
    setValues({});
    if (!name || name === freeName(provider, taken)) setName(next ? freeName(next, taken) : "");
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (chosen && name.trim()) add.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-2" aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`} className="text-sm font-medium">{t("credentials.add")}</h3>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <label htmlFor={`${id}-provider`} className="block text-xs text-[var(--color-muted)]">{t("credentials.provider")}</label>
          <select id={`${id}-provider`} value={provider} required onChange={(e) => pick(e.target.value)} className={FIELD}>
            <option value="">{t("credentials.pickProvider")}</option>
            {providers.map((p) => <option key={p.name} value={p.name}>{p.label}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-name`} className="block text-xs text-[var(--color-muted)]">{t("credentials.name")}</label>
          <input id={`${id}-name`} value={name} required maxLength={100} onChange={(e) => setName(e.target.value)}
            aria-describedby={`${id}-name-hint`} autoComplete="off" spellCheck={false} className={`${FIELD} w-48 font-mono`} />
        </div>
        {chosen && (
          <SecretFields id={id} fields={chosen.fields} values={values} required
            onChange={(f, v) => setValues((old) => ({ ...old, [f]: v }))} />
        )}
        <Button type="submit" disabled={add.isPending || !chosen}>{t("credentials.save")}</Button>
      </div>
      <p id={`${id}-name-hint`} className="text-xs text-[var(--color-muted)]">{t("credentials.nameHint")}</p>
      {add.isPending && <p className="text-sm text-[var(--color-muted)]">{t("credentials.checking")}</p>}
      {add.isSuccess && (
        <p role="status" className="text-sm">
          {t("credentials.added", { name: add.data.created.name })}{" "}
          {add.data.verdict && <Verdict verdict={add.data.verdict} status={add.data.verdict.status} />}
        </p>
      )}
      {add.isError && <ErrorState error={add.error} />}
    </form>
  );
}
